import React, { useState, useEffect, ChangeEvent, useContext } from 'react';
import { ImageUpload } from '@app/pages/directoryManager/imageUpload/imageUpload.component';
import { FormGroup, FormControl, Grid, Box, InputAdornment, IconButton, Typography } from '@material-ui/core';
import { PasslySelect, PasslyCheckbox } from '@app/components/Forms';
import { appLibraryService, policiesAuthService } from '@app/services';
import { ModalComponent, ModalActionsComponent } from '@app/components/Modal';
import { IPolicyAuth } from '@app/models';
import {
    SUPPLIED_CREDENTIALS_OPTIONS,
    suppliedCredentialTypes,
    IWorkflowStep,
    APP_ICON_DIMENSIONS
} from '@app/pages/ssoManager/addEditApplication.enums';
import { generateWorkflowSteps, isKaseyaOne, isWorkflowStepInput, isWorkflowStepPassword } from '@app/helpers/addEditApplication.helpers';
import AppsContext from './appContext';
import { useTypedTranslation } from '@app/hooks';
import { onPressEnterModal } from '@app/helpers';
import { PasslyButton, PasslyTextField } from '@app/components';
import { PasslyTooltip } from '@app/componentsV2';

interface IProps {
    isWorkflowApp: () => boolean;
    isCatalogApp: () => boolean;
    workflowValues: any;
    handleWorkflowValuesChange: (workflowValues: any) => void;
    suppliedCredential: string;
    handleSuppliedCredentialChange: (suppliedCredential: string) => void;
    update?: (data: any) => void;
    error: (bool: boolean) => void;
    addingApp?: boolean;
}

interface IInputErrors {
    nameInputError: boolean;
    passwordInputError: string[];
}

export const ApplicationConfiguration: React.SFC<IProps> = ({
    isWorkflowApp,
    error,
    workflowValues,
    handleWorkflowValuesChange,
    suppliedCredential,
    handleSuppliedCredentialChange,
    addingApp
}: IProps) => {
    const { IconUrl, DisplayName, Enabled, Workflow, PolicyId, Id, ServiceConfiguration, updateState } = useContext(AppsContext);

    const { translation } = useTypedTranslation();

    const [availablePolicies, setAvailablePolicies] = useState<IPolicyAuth[]>([]);

    const [modalData, setModalData] = useState(IconUrl);
    const [openModal, setOpenModal] = useState<boolean>(false);

    const [workflowSteps, setWorkflowSteps] = useState(Workflow ? generateWorkflowSteps(Workflow) : []);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [inputError, setInputError] = useState<IInputErrors>({
        nameInputError: false,
        passwordInputError: []
    });

    useEffect(() => {
        policiesAuthService.getAuthPolicies(handleGetPoliciesError, handleGetPoliciesSuccess);
    }, []);

    useEffect(() => {
        let hasWorkflowError = false;

        for (let key in workflowValues) {
            if (workflowValues[key] === '') {
                hasWorkflowError = true;
            }
        }
        error(inputError.nameInputError || hasWorkflowError);
    }, [inputError.nameInputError, workflowValues]);

    useEffect(() => {
        DisplayName?.length
            ? setInputError((prevState: IInputErrors) => ({ ...prevState, nameInputError: false }))
            : setInputError((prevState: IInputErrors) => ({ ...prevState, nameInputError: true }));
    }, [DisplayName]);

    // IMAGE
    const handleOnImageChange = (data: string) => {
        setModalData(data);
    };

    const handleIconUpload = (data: any, handleError: Function, handleSuccess: Function) => {
        return appLibraryService.updateAppIcon(data, handleError, handleSuccess, Id);
    };

    //  IMAGE - modal
    const openChangeImageModal = () => {
        setOpenModal(true);
    };

    const closeChangeImageModal = () => {
        setOpenModal(false);
    };

    const handleModalSave = () => {
        updateState('IconUrl', modalData);
        closeChangeImageModal();
    };

    const handleCheckboxChange = (checked: boolean, event: any) => {
        updateState(event.target.name, checked);
    };

    const getCheckboxWithTooltip = () => {
        const title = Enabled ? translation('APP_ENABLED_TOOLTIP') : translation('APP_DISABLED_TOOLTIP');

        return (
            <PasslyTooltip title={title} disableFocusListener>
                <span className='app-edit__checkbox-tooltip'>
                    <PasslyCheckbox name='Enabled' checked={Enabled} onChange={handleCheckboxChange} label={translation('APP_ENABLED')} />
                </span>
            </PasslyTooltip>
        );
    };

    // NAME
    const onDisplayNameChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        updateState(name, value);
        DisplayName?.length
            ? setInputError((prevState: IInputErrors) => ({ ...prevState, nameInputError: false }))
            : setInputError((prevState: IInputErrors) => ({ ...prevState, nameInputError: true }));
    };

    const handleGetPoliciesError = ({ value }: any) => {};

    const handleGetPoliciesSuccess = ({ data }: any) => {
        setAvailablePolicies(data.value);
        if (PolicyId === '' || PolicyId === undefined) {
            updateState('PolicyId', data.value[0].Id);
        }
    };

    const isSuppliedCredentialsAdmin = () => {
        return suppliedCredential === suppliedCredentialTypes.admin;
    };

    const onCredentialSelectChange = ({ target }: React.ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
        if ((target.value as string) === suppliedCredentialTypes.admin) {
            let resetWorkflowValues: any = {};

            workflowSteps?.filter(isWorkflowStepInput).forEach((workflowStep: IWorkflowStep) => {
                resetWorkflowValues[workflowStep.elementval] = '';
            });

            handleWorkflowValuesChange(resetWorkflowValues);
        }

        handleSuppliedCredentialChange(target.value as string);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const onAdminSuppliedCredentialsChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, workflowStep: IWorkflowStep) => {
        const { value } = e.target;

        let workflowValuesCopy = { ...workflowValues };
        workflowValuesCopy[workflowStep.elementval] = value;

        handleWorkflowValuesChange(workflowValuesCopy);
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('SELECT_IMAGE_TITLE')}
                modalSwitcherCallback={closeChangeImageModal}
                localModalSwitcher={openModal}
                form={
                    <>
                        <Box p={4} onKeyPress={onPressEnterModal(handleModalSave)}>
                            <ImageUpload
                                width={APP_ICON_DIMENSIONS.width}
                                height={APP_ICON_DIMENSIONS.height}
                                currentImageUrl={IconUrl}
                                onFileUpload={handleIconUpload}
                                onImageChange={handleOnImageChange}
                                imageRequirements={'APP_IMAGE_UPLOAD_REQUIREMENTS'}
                            />
                        </Box>
                        <ModalActionsComponent
                            primaryBtn={translation('SAVE_CHANGES')}
                            secondaryBtn={translation('CANCEL')}
                            onClickPrimary={handleModalSave}
                            onClickSecondary={closeChangeImageModal}
                        />
                    </>
                }
            />
            <Grid container justifyContent='space-between' className='configpanel-row'>
                <Grid item xs={12} lg={5} className='configpanel-left'>
                    <Box p={2}>
                        <form className='app-edit__form--configuration'>
                            <FormGroup>
                                {/* selected image */}
                                <FormControl>
                                    <div className='app-edit__form--configuration__image'>
                                        <div className='app-edit__image__wrapper shadow shadow--box' onClick={openChangeImageModal}>
                                            <img src={IconUrl} alt='' className='app-edit__image' />
                                        </div>
                                        <PasslyButton variant='contained' color='primary' onClick={openChangeImageModal}>
                                            {translation('APP_SELECT_ICON')}
                                        </PasslyButton>
                                    </div>
                                </FormControl>
                                {/* enable application checkbox */}
                                {isKaseyaOne(ServiceConfiguration) ? (
                                    getCheckboxWithTooltip()
                                ) : (
                                    <PasslyCheckbox
                                        name='Enabled'
                                        checked={Enabled}
                                        onChange={handleCheckboxChange}
                                        label={translation('APP_ENABLED')}
                                    />
                                )}

                                {isKaseyaOne(ServiceConfiguration) ? (
                                    <div className='form__row'>
                                        <Typography variant='subtitle1'>{`${translation('APP_DISCLAIMER_TITLE')}: ${translation(
                                            'APP_DISCLAIMER_DESCRIPTION'
                                        )}`}</Typography>
                                    </div>
                                ) : null}

                                {/* app name */}
                                <div className='form__row'>
                                    <PasslyTextField
                                        autoComplete='Display Name'
                                        label={translation('APP_NAME_LABEL')}
                                        name='DisplayName'
                                        value={DisplayName}
                                        onChange={onDisplayNameChange}
                                        id='displayName-input'
                                        required
                                        error={inputError.nameInputError}
                                        fullWidth
                                    />
                                </div>

                                {/* authentication policy */}
                                <div className='form__row'>
                                    <PasslySelect
                                        disabled={availablePolicies?.length === 0}
                                        name='PolicyId'
                                        value={PolicyId}
                                        id='authPolicyId-input'
                                        label={'APP_AUTH_POLICY'}
                                        onChange={({ target: { name, value } }) => updateState(name, value)}
                                        options={availablePolicies}
                                        optionLabel='DisplayName'
                                        optionValue='Id'
                                        appendTo={addingApp ? document.body : undefined}
                                    />
                                </div>
                                {/* <!-- Supplied Credentials --> */}
                                {isWorkflowApp() && (
                                    <>
                                        <div className='form__row'>
                                            <PasslySelect
                                                disabled={SUPPLIED_CREDENTIALS_OPTIONS?.length === 0}
                                                name='SuppliedCredential'
                                                value={suppliedCredential}
                                                onChange={onCredentialSelectChange}
                                                id='suppliedCredential-input'
                                                label={'CREDENTIALS'}
                                                options={SUPPLIED_CREDENTIALS_OPTIONS}
                                                appendTo={addingApp ? document.body : undefined}
                                            />
                                        </div>
                                        {/* WORDKFOW STEPS */}
                                        {isSuppliedCredentialsAdmin() &&
                                            Object.keys(workflowValues).length &&
                                            workflowSteps?.filter(isWorkflowStepInput).map((workflowStep: IWorkflowStep, index: number) => {
                                                return !isWorkflowStepPassword(workflowStep) ? (
                                                    <div className='form__row' key={index}>
                                                        <PasslyTextField
                                                            fullWidth
                                                            autoComplete='adminSuppliedUsername'
                                                            label={workflowStep.value}
                                                            value={workflowValues[workflowStep.elementval]}
                                                            onChange={(e) => onAdminSuppliedCredentialsChange(e, workflowStep)}
                                                            id={workflowStep.elementval}
                                                        />
                                                    </div>
                                                ) : (
                                                    <FormControl key={index} required error={!workflowValues[workflowStep.elementval].length}>
                                                        <div className='form__row' key={index}>
                                                            <PasslyTextField
                                                                fullWidth
                                                                name='confirmPassword'
                                                                type={showPassword ? 'text' : 'password'}
                                                                label={workflowStep.value}
                                                                autoComplete='adminPassword-input'
                                                                value={workflowValues[workflowStep.elementval]}
                                                                onChange={(e) => onAdminSuppliedCredentialsChange(e, workflowStep)}
                                                                id={workflowStep.elementval}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <InputAdornment position='end'>
                                                                            <IconButton
                                                                                size='small'
                                                                                onClick={() => setShowPassword(!showPassword)}
                                                                                onMouseDown={handleMouseDownPassword}
                                                                            >
                                                                                {showPassword ? (
                                                                                    <i className='fa-regular fa-eye-slash' />
                                                                                ) : (
                                                                                    <i className='fa-regular fa-eye' style={{ width: '22.5px' }} />
                                                                                )}
                                                                            </IconButton>
                                                                        </InputAdornment>
                                                                    )
                                                                }}
                                                            />
                                                        </div>
                                                    </FormControl>
                                                );
                                            })}
                                    </>
                                )}
                            </FormGroup>
                        </form>
                    </Box>
                </Grid>
                <Grid item xs={12} lg={6} className='configpanel-right'>
                    <Box p={4}>
                        {isWorkflowApp() ? (
                            <div dangerouslySetInnerHTML={{ __html: translation('APP_CONFIG_HELPER_HEADER_WITH_CREDENTIAL') }} />
                        ) : isKaseyaOne(ServiceConfiguration) ? (
                            <div dangerouslySetInnerHTML={{ __html: translation('APP_CONFIG_HELPER_KASEYA_ONE_HEADER') }} />) : (
                            <div dangerouslySetInnerHTML={{ __html: translation('APP_CONFIG_HELPER_HEADER') }} />
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};
